import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip as BsTooltip,
} from "react-bootstrap";
import { MdAdd, MdDelete, MdEdit, MdSearch, MdUpload } from "react-icons/md";
import { couriersAPI } from "../services/api";
import CustomPagination from "../components/comman/CustomPagination";
import CustomAction from "../components/comman/CustomAction";
import DeleteModal from "../components/modals/delete-modal/DeleteModal";
import { showToast } from "../services/toast";
import CourierModal from "../components/modals/courier/CourierModal";
import { DEFAULT_PAGE_OPTIONS } from "../utils/constants";
import SimpleTable from "../components/comman/custom-table/SimpleTable";
import ImportCourierModal from "../components/modals/courier/ImportCourierModal";

// small helpers
const pick = (...vals) =>
  vals.find((v) => v !== undefined && v !== null && v !== "");
const isUrl = (s = "") => /^https?:\/\//i.test(s);

const Couriers = () => {
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(false);

  // search/pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // import modal
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);

  // add/edit modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [errors, setErrors] = useState({});

  // delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courierToDelete, setCourierToDelete] = useState(null);

  // form state
  const [form, setForm] = useState({
    name: "",
    short_name: "",
    website: "",
    tracking_url: "",
    service_type: "",
    rate_card_ref: "",
    api_details: "", // keep as string; backend may parse JSON
    status: "",
    remarks: "",
  });

  // debounced search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // fetch list
  const fetchCouriers = async (opts = {}) => {
    setLoading(true);
    try {
      const res = await couriersAPI.getCouriers({
        page: opts.page ?? currentPage,
        limit: opts.limit ?? pageSize,
        search: opts.search ?? debouncedSearch,
      });

      if (res?.isSuccess) {
        const list =
          res.data?.couriers ??
          res.data?.courierMasters ??
          res.data?.data ??
          [];

        const pg = res.data?.pagination ?? {
          total: list.length,
          page: 1,
          limit: pageSize,
          totalPages: 1,
        };

        setCouriers(list);
        setTotal(pg.total || 0);
        setTotalPages(pg.totalPages || 1);

        if (pg.page && pg.page !== currentPage) setCurrentPage(pg.page);
        if (pg.limit && pg.limit !== pageSize) setPageSize(pg.limit);
      } else {
        showToast("error", res?.message || "Failed to load couriers");
      }
    } catch (e) {
      showToast(
        "error",
        e?.response?.data?.message || "Failed to load couriers"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCouriers({
      page: currentPage,
      limit: pageSize,
      search: debouncedSearch,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearch]);

  // validation
  const validateForm = () => {
    const e = {};
    if (!String(form.name || "").trim()) e.name = "Courier name is required.";
    if (!String(form.short_name || "").trim())
      e.short_name = "Short name is required.";
    if (form.website && !isUrl(form.website))
      e.website = "Website must be a valid URL.";
    if (form.tracking_url && !isUrl(form.tracking_url))
      e.tracking_url = "Tracking URL must be valid.";
    // status optional rule — enforce if you want:
    // if (!form.status) e.status = "Status is required.";
    return e;
  };

  // create
  const startCreate = () => {
    setEditing(null);
    setErrors({});
    setForm({
      name: "",
      short_name: "",
      website: "",
      tracking_url: "",
      service_type: "",
      rate_card_ref: "",
      api_details: "",
      status: "",
      remarks: "",
    });
    setShowModal(true);
  };

  // edit
  const startEdit = (c) => {
    setEditing(c);
    setErrors({});
    setForm({
      name: pick(c.name, c.courier_name, ""),
      short_name: pick(c.short_name, c.shortName, ""),
      website: c.website || "",
      tracking_url: pick(c.tracking_url, c.trackingUrl, ""),
      service_type: pick(c.service_type, c.serviceType, ""),
      rate_card_ref: pick(c.rate_card_ref, c.rateCardRef, ""),
      api_details:
        typeof c.api_details === "string"
          ? c.api_details
          : JSON.stringify(c.api_details || "", null, 2),
      status: c.status ?? "",
      remarks: c.remarks ?? "",
    });
    setShowModal(true);
  };

  // save
  const saveCourier = async (e) => {
    e.preventDefault();
    const vErrors = validateForm();
    if (Object.keys(vErrors).length) {
      setErrors(vErrors);
      setTimeout(() => {
        const first = document.querySelector(".is-invalid");
        if (first) first.focus();
      }, 0);
      showToast("error", "Please fix the highlighted errors.");
      return;
    }

    try {
      const payload = {
        name: form.name,
        short_name: form.short_name,
        website: form.website,
        tracking_url: form.tracking_url,
        service_type: form.service_type,
        rate_card_ref: form.rate_card_ref,
        api_details: form.api_details, // keep as text; BE can parse JSON
        status: form.status,
        remarks: form.remarks,
      };

      if (editing) {
        await couriersAPI.updateCourier(editing.id, payload);
        showToast("success", "Courier updated");
      } else {
        await couriersAPI.createCourier(payload);
        showToast("success", "Courier created");
      }

      setShowModal(false);
      fetchCouriers();
    } catch (err) {
      showToast("error", err?.response?.data?.message || "Save failed");
    }
  };

  // delete
  const removeCourier = async () => {
    if (!courierToDelete) return;
    try {
      await couriersAPI.deleteCourier(courierToDelete.id);
      showToast("success", "Courier deleted");
      fetchCouriers();
    } catch (err) {
      showToast("error", err?.response?.data?.message || "Delete failed");
    } finally {
      setShowDeleteModal(false);
      setCourierToDelete(null);
    }
  };

  // import
  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportReport(null);
    setImportSuccess(null);

    try {
      const res = await couriersAPI.importCouriers(file);

      if (res?.isSuccess) {
        const hasErrors = !!res?.data?.errors?.length;

        if (hasErrors) {
          setImportReport(res?.data);
          setImportSuccess(false);
          setShowImport(true);
          showToast("error", "Import completed with validation errors");
        } else {
          showToast("success", "Couriers imported successfully");
          setImportReport(null);
          setImportSuccess(null);
          setShowImport(false);
          fetchCouriers();
        }
      } else {
        setImportReport(res?.data || null);
        setImportSuccess(false);
        setShowImport(true);
        showToast("error", res?.message || "Import failed");
      }
    } catch (err) {
      const data = err?.response?.data?.data;
      const message = err?.response?.data?.message || "Import failed!";
      showToast("error", message);

      if (data && data !== 0) {
        setImportReport(data);
        setImportSuccess(false);
        setShowImport(true);
      } else {
        setImportReport(null);
        setImportSuccess(null);
        setShowImport(false);
      }
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const downloadValidationErrorsCsv = (report) => {
    if (!report?.errors?.length) return;

    const dataKeys = Array.from(
      report.errors.reduce((set, r) => {
        Object.keys(r.data || {}).forEach((k) => set.add(k));
        return set;
      }, new Set())
    );

    const header = ["row", "errors", ...dataKeys].join(",");
    const lines = report.errors.map((r) => {
      const errMsg = (r.errors || []).join("; ");
      const cells = dataKeys.map((k) => {
        let v = r.data?.[k];
        if (v === null || v === undefined) v = "";
        v = String(v).replace(/"/g, '""');
        return `"${v}"`;
      });
      return [`"${r.row}"`, `"${errMsg.replace(/"/g, '""')}"`, ...cells].join(
        ","
      );
    });

    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `courier-import-validation-errors-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessor: (r) => pick(r.name, r.courier_name),
        minWidth: 220,
        className: "text-truncate",
        cell: (v) =>
          v ? (
            <span title={v}>{v}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Short",
        accessor: (r) => pick(r.short_name, r.shortName),
        minWidth: 120,
        className: "text-truncate",
        cell: (v) =>
          v ? (
            <span title={v}>{v}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Website",
        accessor: (r) => r.website,
        minWidth: 200,
        className: "text-truncate",
        cell: (v) =>
          v ? (
            <a href={v} target="_blank" rel="noreferrer" title={v}>
              {v}
            </a>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Tracking URL",
        accessor: (r) => pick(r.tracking_url, r.trackingUrl),
        minWidth: 260,
        className: "text-truncate",
        cell: (v) =>
          v ? (
            <a href={v} target="_blank" rel="noreferrer" title={v}>
              {v}
            </a>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Service Type",
        accessor: (r) => pick(r.service_type, r.serviceType),
      },
      {
        header: "Rate Card Ref",
        accessor: (r) => pick(r.rate_card_ref, r.rateCardRef),
      },
      {
        header: "Status",
        accessor: (r) => r.status ?? "—",
        className: "text-capitalize",
      },
      {
        header: "Remarks",
        accessor: (r) => r.remarks,
        minWidth: 240,
        className: "text-truncate",
        cell: (v) =>
          v ? (
            <span title={v}>{v}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        id: "created_at",
        header: "Created At",
        accessor: "created_at", // change to "createdAt" if your API uses camelCase
        minWidth: 160,
        sortType: "date",
        cell: (v, row) => {
          // fallback if API returns createdAt instead of created_at
          const raw = v ?? row?.created_at;
          if (!raw) return "—";
          const dt = new Date(raw);
          if (isNaN(+dt)) return "—";

          const dateStr = dt.toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
          const timeStr = dt.toLocaleString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <div className="d-flex flex-column lh-sm gap-1">
              <strong>{dateStr}</strong>
              <p className="m-0">{timeStr}</p>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        isActions: true, // SimpleTable knows to render Edit/Delete buttons
        minWidth: 140,
        className: "text-nowrap",
      },
    ],
    []
  );
  return (
    <>
      <div className="h-100 overflow-auto position-relative z-1">
        <div className="d-flex justify-content-between align-items-center mb-1 p-3 sticky-top bg-body">
          <h4 className="mb-0">Courier Module</h4>
          <div className="d-flex align-items-end gap-2">
            <InputGroup className="search-bar border-primary rounded-2">
              <Form.Control
                placeholder="Search couriers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <InputGroup.Text>
                <MdSearch />
              </InputGroup.Text>
            </InputGroup>
            <CustomAction
              actions={[
                {
                  label: "Import Excel",
                  icon: MdUpload,
                  onClick: () => setShowImport(true),
                },
                { label: "Add Courier", icon: MdAdd, onClick: startCreate },
              ]}
            />
          </div>
        </div>

        <div className="content-cover px-3 position-relative">
          {/* <div className="table-cover">
            {loading ? (
              <div className="text-center py-3">
                <Spinner animation="border" />
              </div>
            ) : (
              <>
                <Table hover bordered className="position-relative z-1 mb-0">
                  <thead className="sticky-top">
                    <tr>
                      <th>Name</th>
                      <th>Short</th>
                      <th>Website</th>
                      <th>Tracking URL</th>
                      <th>Service Type</th>
                      <th>Rate Card Ref</th>
                      <th>Status</th>
                      <th>Remarks</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {couriers.map((c) => (
                      <tr key={c.id}>
                        <td className="text-truncate" style={{ maxWidth: 220 }}>
                          {pick(c.name, c.courier_name, "—")}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 120 }}>
                          {c.short_name || c.shortName || "—"}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 200 }}>
                          {c.website ? (
                            <a
                              href={c.website}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {c.website}
                            </a>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 260 }}>
                          {c.tracking_url || c.trackingUrl ? (
                            <a
                              href={c.tracking_url || c.trackingUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {c.tracking_url || c.trackingUrl}
                            </a>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>{c.service_type || c.serviceType || "—"}</td>
                        <td>{c.rate_card_ref || c.rateCardRef || "—"}</td>
                        <td className="text-capitalize">
                          {String(c.status ?? "—")}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 240 }}>
                          {c.remarks || "—"}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            className="table-btn me-2"
                            onClick={() => startEdit(c)}
                            title="Edit"
                          >
                            <MdEdit />
                          </Button>
                          <Button
                            size="sm"
                            className="table-btn"
                            onClick={() => {
                              setCourierToDelete(c);
                              setShowDeleteModal(true);
                            }}
                            title="Delete"
                          >
                            <MdDelete />
                          </Button>
                        </td>
                      </tr>
                    ))}

                    {couriers.length === 0 && !loading && (
                      <tr>
                        <td colSpan={9} className="text-center text-muted">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}
          </div> */}
          <SimpleTable
            data={couriers}
            columns={columns}
            loading={loading}
            keyField="id"
            emptyMessage="No records found"
            responsive
            // wire actions
            onEdit={(row) => startEdit(row)}
            onDelete={(row) => {
              setCourierToDelete(row);
              setShowDeleteModal(true);
            }}
          />
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            loading={loading}
            onPageChange={setCurrentPage}
            className={"sticky-bottom"}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrentPage={setCurrentPage}
            total={total}
            perPageOptions={DEFAULT_PAGE_OPTIONS}
          />
        </div>
      </div>

      {/* Delete modal */}
      <DeleteModal
        confirmDelete={removeCourier}
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        message={`Are you sure you want to delete <strong>${
          courierToDelete?.name || courierToDelete?.courier_name
        }</strong>?`}
      />

      {/* Import modal */}
      <ImportCourierModal
        show={showImport}
        importSuccess={importSuccess}
        importReport={importReport}
        importing={importing}
        onHide={() => {
          if (importing) return;
          setImportReport(null);
          setImportSuccess(null);
          setShowImport(false);
        }}
        onClose={() => {
          setImportReport(null);
          setImportSuccess(null);
          setShowImport(false);
        }}
        downloadValidationErrorsCsv={downloadValidationErrorsCsv}
        onImport={onImport}
      />

      {/* Add/Edit modal */}
      <CourierModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setErrors({});
        }}
        isEdit={editing}
        form={form}
        setForm={setForm}
        saveCourier={saveCourier}
        errors={errors}
      />
    </>
  );
};

export default Couriers;
